import { useState, useEffect } from 'react';
import { Modal, Button, Input, Dropdown } from '@plick/ui';
import { toast } from 'react-hot-toast';
import {
  REPORT_TYPES,
  ReportFormData,
  ReportFormErrors,
  MIN_DESCRIPTION_LENGTH,
  MAX_DESCRIPTION_LENGTH,
} from '@utils/schemas/reportSchema';
import { Commerce } from '@interfaces/general';

// Client is ready
import { supabaseSupport } from '@utils/supabase/supportClient';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  merchantId: string;
  commerce: Commerce;
}

const SupportModal = ({ isOpen, onClose, merchantId, commerce }: SupportModalProps) => {
  const [formData, setFormData] = useState<ReportFormData>({
    merchantId,
    reportTypeCode: '',
    customTypeText: '',
    description: '',
    commerce,
  });

  const [errors, setErrors] = useState<ReportFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        merchantId,
        reportTypeCode: '',
        customTypeText: '',
        description: '',
        commerce,
      });
      setErrors({});
    }
  }, [isOpen, merchantId]);

  const validate = (): boolean => {
    const newErrors: ReportFormErrors = {};
    let isValid = true;

    if (!formData.reportTypeCode) {
      newErrors.reportTypeCode = 'Selecciona un tipo de reporte';
      isValid = false;
    }

    if (
      formData.reportTypeCode === 'other' &&
      (!formData.customTypeText || formData.customTypeText.trim().length < 3)
    ) {
      newErrors.customTypeText = 'Especifica el tipo (mín. 3 caracteres)';
      isValid = false;
    }

    if (!formData.description || formData.description.length < MIN_DESCRIPTION_LENGTH) {
      newErrors.description = `Mínimo ${MIN_DESCRIPTION_LENGTH} caracteres`;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);

    try {
      const { error } = await supabaseSupport.functions.invoke('submit-report', {
        body: formData,
      });

      if (error) throw error;

      toast.success('Reporte enviado exitosamente');
      onClose();
    } catch {
      toast.error('Error al enviar el reporte. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <h3 className="text-xl font-bold text-primary mb-2">Soporte y Reportes</h3>
        <p className="text-secondary mb-6 text-sm">
          ¿En qué podemos ayudarte? Tu reporte nos ayuda a mejorar la calidad del servicio.
        </p>

        <div className="flex flex-col gap-5">
          {/* Tipo de Reporte */}
          <div className="w-full">
            <label className="block text-sm font-semibold text-primary mb-1.5">
              Tipo de Reporte <span className="text-red-500">*</span>
            </label>
            <Dropdown
              value={formData.reportTypeCode}
              options={REPORT_TYPES.map((t) => ({
                value: t.value,
                children: (
                  <div className="flex items-center gap-3 py-1">
                    <span className="font-medium text-gray-700">{t.label}</span>
                  </div>
                ),
              }))}
              onChange={(val) => setFormData((prev) => ({ ...prev, reportTypeCode: String(val) }))}
              error={errors.reportTypeCode}
            />
          </div>

          {/* Input Custom si es 'other' */}
          {formData.reportTypeCode === 'other' ? (
            <div className="w-full animate-in fade-in slide-in-from-top-2 duration-300">
              <Input.Text
                label="Especifica el motivo"
                name="customTypeText"
                value={formData.customTypeText}
                onChange={(val) => setFormData((prev) => ({ ...prev, customTypeText: val }))}
                placeholder="Ej. Problema con la impresora..."
                error={errors.customTypeText}
              />
            </div>
          ) : null}

          {/* Descripción */}
          <div className="w-full">
            <Input.TextArea
              label="Descripción detallada"
              name="description"
              value={formData.description}
              onChange={(val) => setFormData((prev) => ({ ...prev, description: val }))}
              placeholder="Describe el problema con el mayor detalle posible..."
            />
            <div className="flex justify-between mt-1.5 px-1">
              {errors.description ? (
                <span className="text-xs text-red-500 font-medium">{errors.description}</span>
              ) : (
                <span></span>
              )}
              <span
                className={`text-xs font-medium ${
                  formData.description.length > MAX_DESCRIPTION_LENGTH ? 'text-red-500' : 'text-gray-400'
                }`}
              >
                {formData.description.length}/{MAX_DESCRIPTION_LENGTH}
              </span>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-100">
          <Button.Tertiary text="CANCELAR" onClick={onClose} disabled={isLoading} />
          <Button.Primary
            text={isLoading ? 'ENVIANDO...' : 'ENVIAR REPORTE'}
            onClick={handleSubmit}
            disabled={isLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SupportModal;
